import React, { FC, useEffect, useMemo, useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, CloseIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { Checkbox } from '../../../../components/checkbox/index';
import { FormInput } from '../../../../components/input/index';
import { Cursor, Tooltip } from '../../../../components/tooltip/index';
import {
  b2bStakeholderTypes,
  b2cStakeholderTypes,
  StakeholderRole,
  StakeholderType,
} from '../../../../types/stakeholder.types';
import BirthdayDateSelect from '../date-combobox/birthday-date-select';
import IncorporationCompanyDateSelect from '../date-combobox/incorporation-company-date-select';
import PassportExpiryDateSelect from '../date-combobox/passport-expiry-date-select';
import IncorporationCountryCombobox, { SelectedCountry } from '../incorporation-country-combobox';
import NationalityCombobox, { SelectedNationality } from '../nationality-combobox';
import PhoneNumberCombobox, { PhoneCountry } from '../phone-number-combobox';
import { ChildrenFormProps } from '../stakeholder-actions.types';
import {
  FormSchema,
  stakeholderTypeDefaultPermissionRestriction,
  stepOne as stepOneFormSchema,
} from '../stakeholder-actions.validation';
import StakeholderTypeCombobox from '../stakeholder-type-combobox';

export type StepOneProps = ChildrenFormProps;

export const StepOne: FC<StepOneProps> = ({
  handleCloseModal,
  nextFormStep,
  control,
  setFormData,
  lockMode,
  filedState,
  disabled,
  resetField,
  stakeholder,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const [isOpenEquityOptions, setOpenEquityOptions] = useState(
    stepOne?.access?.position || stepOne?.access?.equity || stepOne?.access?.companyTable,
  );
  const [isAllEquityOptionsChecked, setAllEquityOptionsChecked] = useState(true);
  const { success } = stepOneFormSchema.safeParse(stepOne);

  const {
    fields: subEmailFields,
    append: addSubEmail,
    remove: removeSubEmail,
  } = useFieldArray<FormSchema>({
    control,
    name: 'stepOne.subEmails',
  });

  const handleAddSubEmail = () => {
    addSubEmail({ value: '' });
  };

  const handleRemoveSubEmail = (index: number) => {
    removeSubEmail(index);
  };

  const emailListener = stepOne?.email;

  const selectedNationality = stepOne?.human?.nationality;

  const selectedIncorporationCountry = stepOne?.company?.incorporationCountry;

  const selectedPhoneNumber = stepOne?.phoneNumber;

  const selectedPhoneCountry = stepOne?.phoneCountry;

  const selectedStakeholderType = stepOne?.stakeholderType;

  const isB2BStakeholderType = useMemo(
    () => (selectedStakeholderType ? b2bStakeholderTypes.includes(selectedStakeholderType) : false),
    [selectedStakeholderType],
  );

  const isB2CStakeholderType = useMemo(
    () => (selectedStakeholderType ? b2cStakeholderTypes.includes(selectedStakeholderType) : false),
    [selectedStakeholderType],
  );

  const isValid = success && !filedState('stepOne').invalid;

  useEffect(() => {
    const anyChecked =
      stepOne?.access?.position || stepOne?.access?.equity || stepOne?.access?.companyTable;

    setAllEquityOptionsChecked(Boolean(anyChecked));
  }, [stepOne?.access?.position, stepOne?.access?.equity, stepOne?.access?.companyTable]);

  const impactPermissionRestriction = (type: StakeholderType) => {
    setFormData(
      'stepOne.access',
      stakeholderTypeDefaultPermissionRestriction[type] ??
        stakeholderTypeDefaultPermissionRestriction.default,
    );
  };

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <FormInput
          control={control}
          name="stepOne.fullName"
          placeholder={<AppFormattedMessage id="FULL_NAME" />}
        />
        <FormInput
          control={control}
          name="stepOne.email"
          placeholder={<AppFormattedMessage id="EMAIL_MAIN" />}
        />

        <StakeholderTypeCombobox
          control={control}
          disabled={disabled}
          onSelect={(type) => {
            impactPermissionRestriction(type);
            resetField('stepOne.company');
            resetField('stepOne.human');
            resetField('stepOne.phoneNumber');
            resetField('stepOne.address');
          }}
        />

        {isB2CStakeholderType && stakeholder?.role !== StakeholderRole.ADMIN && (
          <div className="flex flex-col gap-4">
            {subEmailFields.map((field, i) => (
              <div className="flex w-full items-start justify-between gap-4" key={field.id}>
                <FormInput
                  control={control}
                  name={`stepOne.subEmails.${i}.value`}
                  placeholder={<AppFormattedMessage id="SUB_EMAIL_PLACEHOLDER" />}
                  wrapperClassName="w-full"
                />
                <Button
                  className="mt-5 flex h-fit w-fit items-center justify-center"
                  onClick={() => handleRemoveSubEmail(i)}
                  styleType="NONE"
                >
                  <CloseIcon />
                </Button>
              </div>
            ))}

            {emailListener && (
              <Tooltip
                content={<AppFormattedMessage id="YOU_HAVE_REACHED_SUB_EMAIL_LIMIT" />}
                cursor={Cursor.POINTER}
                disabled={subEmailFields.length !== 10}
              >
                <Button
                  className="w-fit rounded border border-gray-300 bg-gray-25 px-4 py-[10px] text-sm font-[450] text-gray-700 disabled:bg-transparent disabled:ring-0"
                  disabled={subEmailFields.length === 10}
                  onClick={handleAddSubEmail}
                  styleType="NONE"
                >
                  + <AppFormattedMessage id="SUB_EMAIL" />
                </Button>
              </Tooltip>
            )}
          </div>
        )}

        {isB2BStakeholderType && (
          <>
            <FormInput
              control={control}
              name="stepOne.human.passportNo"
              placeholder={<AppFormattedMessage id="PASSPORT_NO_OPTIONAL" />}
            />
            <PassportExpiryDateSelect
              control={control}
              isNeedDefaultValue={lockMode}
              setValue={setFormData}
            />
            <NationalityCombobox
              control={control}
              selectedNationality={selectedNationality as SelectedNationality}
              setFormData={setFormData}
            />
            <BirthdayDateSelect
              control={control}
              isNeedDefaultValue={lockMode}
              setValue={setFormData}
            />
          </>
        )}
        {isB2CStakeholderType && (
          <>
            <IncorporationCountryCombobox
              control={control}
              selectedIncorporationCountry={selectedIncorporationCountry as SelectedCountry}
            />
            <FormInput
              control={control}
              name="stepOne.company.identifier"
              placeholder={<AppFormattedMessage id="COMPANY_IDENTIFIER" />}
            />

            <IncorporationCompanyDateSelect
              control={control}
              isNeedDefaultValue={lockMode}
              setValue={setFormData}
            />
          </>
        )}

        {(isB2CStakeholderType || isB2BStakeholderType) && (
          <>
            <PhoneNumberCombobox
              onPhoneChange={(phoneNumber) =>
                setFormData('stepOne.phoneNumber', phoneNumber, {
                  shouldValidate: true,
                })
              }
              onSelectCountry={(country) => setFormData('stepOne.phoneCountry', country)}
              selectedPhoneNumber={{
                number: selectedPhoneNumber || '',
                country: selectedPhoneCountry as PhoneCountry,
              }}
            />
            <FormInput
              control={control}
              name="stepOne.address"
              placeholder={
                <>
                  <AppFormattedMessage id="ADDRESS_OPTIONAL" />
                </>
              }
            />
            <div className="flex h-fit w-full flex-col gap-3 rounded bg-gray-50 p-3 pl-2">
              <span className="text-base font-semibold text-gray-700">
                <AppFormattedMessage id="ACCESS_AND_PERMISSIONS" />
              </span>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-1">
                  <Checkbox
                    checked={stepOne?.access?.capTable}
                    onChange={(e) => {
                      setFormData('stepOne.access.capTable', e);
                    }}
                  />
                  <span className="text-sm font-[450] text-gray-700">
                    <AppFormattedMessage id="CAP_TABLE" />
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Checkbox
                    checked={isAllEquityOptionsChecked}
                    onChange={(e) => {
                      setAllEquityOptionsChecked(e);
                      setFormData('stepOne.access.position', e);
                      setFormData('stepOne.access.equity', e);
                      setFormData('stepOne.access.companyTable', e);
                    }}
                  />
                  <Button
                    className="flex h-fit w-fit items-center gap-1"
                    onClick={() => setOpenEquityOptions((prev) => !prev)}
                    styleType="NONE"
                    type="button"
                  >
                    <span className="text-sm font-[450] text-gray-700">
                      <AppFormattedMessage id="MY_HOLDING_EQUITY" />
                    </span>
                    <ChevronDownIcon
                      className={twMerge(
                        'size-4 transition-all',
                        isOpenEquityOptions && 'rotate-180',
                      )}
                    />
                  </Button>
                </div>

                <div
                  className={twMerge(
                    'ml-[30px] flex h-0 flex-col gap-2 overflow-hidden transition-all duration-500',
                    isOpenEquityOptions && 'h-fit',
                  )}
                >
                  <div className="flex items-center gap-1">
                    <Checkbox
                      checked={stepOne?.access?.position}
                      onChange={(e) => setFormData('stepOne.access.position', e)}
                    />
                    <span className="text-sm font-[450] text-gray-700">
                      <AppFormattedMessage id="YOUR_POSITION_ISSUED" />
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Checkbox
                      checked={stepOne?.access?.equity}
                      onChange={(e) => setFormData('stepOne.access.equity', e)}
                    />
                    <span className="text-sm font-[450] text-gray-700">
                      <AppFormattedMessage id="EQUITY_AND_CAPITAL_CHANGE" />
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Checkbox
                      checked={stepOne?.access?.companyTable}
                      onChange={(e) => setFormData('stepOne.access.companyTable', e)}
                    />
                    <span className="text-sm font-[450] text-gray-700">
                      Company Table (of{' '}
                      <i>
                        <AppFormattedMessage id="EQUITY_AND_CAPITAL_CHANGE" />
                      </i>
                      )
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? <AppFormattedMessage id="UPDATE" /> : <AppFormattedMessage id="NEXT" />}
          </Button>
        </div>
      </div>
    </>
  );
};
